import styled from "@emotion/styled";
import { filter, pipe } from "callbag-basics";
import subscribe from "callbag-subscribe";
import { noop, throttle, union } from "lodash";
import * as React from "react";

import { DesignToken, RadiusToken } from "@livechat/design-system-react-components";

import { ICannedResponsesProps } from "components/message-box/canned-responses";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-named-as-default
import MessageBox from "components/message-box/legacy/MessageBox";
import { SESSION_STORAGE_DRAFT_KEY } from "constants/tickets/drafts";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { CannedResponseInputMethod, ICannedResponseUsage, trackCannedResponseUsage } from "helpers/canneds";
import { placeCursor } from "helpers/input";
import { doesMessageIncludeCustomText, trimAgentSignature } from "helpers/tickets";
import { IDraft, TicketStatus } from "interfaces/entities/ticket";
import { getItem } from "services/session-storage";
import { actions$ } from "store/action-stream";
import { RequestAction } from "store/entities/actions";
import { TICKET } from "store/entities/tickets/actions";
import { ApplicationsActionNames } from "store/features/applications/actions";
import { IHandlePutMessageParams } from "store/features/applications/interfaces";
import { IAction, IActionWithPayload } from "store/helper";
import { withVar } from "themes/helpers";

import { Attachment, TicketAttachments } from "./TicketAttachments";
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/no-named-as-default
import TicketPrivateMessageSwitch from "./TicketPrivateMessageSwitch";
import { TicketStatusDropdown } from "./TicketStatusDropdown";

interface IProps {
  isSendingMessage: boolean;
  agentSignature: string | null;
  cannedResponsesByShortcuts: ICannedResponsesProps["items"];
  canSendPublicMessage: boolean;
  canSendPrivateMessage: boolean;
  ticketId?: string;
  ticketStatus?: TicketStatus;
  onMessageSend(message: string, isPrivate: boolean, status: TicketStatus, attachments: File[]): void;
  onDraftSave?(message: string, messageText: string, isPrivate: boolean, status: TicketStatus): void;
  onAttachmentsChange?(attachments: Attachment[]): void;
}

interface IState {
  value: string;
  isPrivate: boolean;
  attachments: Attachment[];
  ticketStatus: TicketStatus;
  textareaFocused: boolean;
}

const Wrapper = styled.div`
  background: ${withVar(DesignToken.SurfacePrimaryDefault)};
  border-radius: ${withVar(RadiusToken.Radius3)};
`;

function getAttachmentsToSend(attachments: Attachment[]): File[] {
  return attachments.filter(Boolean) as File[]; // Attachment is File | null
}

function formIsValid(
  message: string,
  isPrivate: boolean,
  ticketStatus: TicketStatus,
  originalTicketStatus: TicketStatus,
  attachments: Attachment[]
): boolean {
  const ticketStatusChanged = !isPrivate && originalTicketStatus && ticketStatus !== originalTicketStatus;
  const messageNotEmpty = message.trim().length > 0;
  const hasAttachments = getAttachmentsToSend(attachments).length > 0;
  const isNewTicket = originalTicketStatus === undefined;

  if (isNewTicket) {
    return messageNotEmpty;
  }
  return ticketStatusChanged || messageNotEmpty || hasAttachments;
}

function getPlaceholder(isPrivate: boolean): string {
  return isPrivate ? "Enter private note visible only to agents" : "Enter message";
}

function getInitialState(ticketId: string, canSendPrivateMessage: boolean, canSendPublicMessage: boolean): IState {
  const ticketDrafts = getItem(SESSION_STORAGE_DRAFT_KEY) || {};
  const draft: IDraft = ticketDrafts[ticketId];

  if (draft) {
    let isPrivate: boolean;
    if (!canSendPrivateMessage) {
      isPrivate = false;
    } else if (canSendPublicMessage) {
      // @ts-expect-error TS(2322): Type 'boolean | undefined' is not assignable to ty... Remove this comment to see the full error message
      // eslint-disable-next-line prefer-destructuring
      isPrivate = draft.isPrivate;
    } else {
      isPrivate = true;
    }

    return {
      value: typeof draft.message === "string" ? draft.message : "",
      isPrivate,
      attachments: [],
      // @ts-expect-error TS(2322): Type 'TicketStatus | undefined' is not assignable ... Remove this comment to see the full error message
      ticketStatus: draft.status,
      textareaFocused: false,
    };
  }

  return {
    value: "",
    isPrivate: canSendPrivateMessage ? !canSendPublicMessage : false,
    attachments: [],
    ticketStatus: TicketStatus.Pending,
    textareaFocused: false,
  };
}

const ticketMessageSentActions = [TICKET.CREATE[RequestAction.SUCCESS], TICKET.SEND_MESSAGE[RequestAction.SUCCESS]];

export class TicketMessageBox extends React.PureComponent<IProps, IState> {
  // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
  state = getInitialState(this.props.ticketId, this.props.canSendPrivateMessage, this.props.canSendPublicMessage);

  private unsubscribeMessageSentStream: () => void = noop;
  private unsubscribePutMessageStream: () => void = noop;

  private shouldSaveDraft = (value: string): boolean => {
    const { ticketId } = this.props;

    return (
      value.trim().length === 0 ||
      // @ts-expect-error TS(2345)
      doesMessageIncludeCustomText(value, this.props.agentSignature) ||
      // @ts-expect-error TS(2345)
      (!doesMessageIncludeCustomText(value, this.props.agentSignature) &&
        // @ts-expect-error TS(2538): Type 'undefined' cannot be used as an index type.
        !!(getItem(SESSION_STORAGE_DRAFT_KEY) || {})[ticketId])
    );
  };

  private throttledSaveDraft = throttle(() => {
    if (this.shouldSaveDraft(this.state.value) && this.props.onDraftSave) {
      this.props.onDraftSave(this.state.value, this.state.value, this.state.isPrivate, this.state.ticketStatus);
    }
  }, 2000);

  componentDidMount(): void {
    this.unsubscribeMessageSentStream = pipe(
      actions$,
      filter((action: IAction) => ticketMessageSentActions.includes(action.type)),
      subscribe(this.handleMessageSent)
    );

    this.unsubscribePutMessageStream = pipe(
      actions$,
      // @ts-expect-error TS(2345)
      filter((action: IAction) => action.type === ApplicationsActionNames.HANDLE_PUT_MESSAGE),
      subscribe(this.handlePutMessage)
    );
  }

  componentWillUnmount(): void {
    this.unsubscribeMessageSentStream();
    this.unsubscribePutMessageStream();
  }

  private handleMessageSent = (): void => {
    this.setState({
      value: "",
      attachments: [],
    });
  };

  private handlePutMessage = (action: IActionWithPayload<string, IHandlePutMessageParams>): void => {
    const { message } = action.payload;

    this.setState(
      prevState => ({
        value: `${prevState.value} ${message}`,
      }),
      () => {
        if (this.textareaRef) {
          placeCursor(this.textareaRef, this.state.value.length);
        }
      }
    );
  };

  private handleCannedResponseUse = (response: string, method: CannedResponseInputMethod): void => {
    this.usedCannedResponses = union(this.usedCannedResponses, [{ response, method }]);
  };

  private handleModeChange = (): void => {
    this.setState(
      prevState => {
        const { agentSignature } = this.props;
        let newValue = prevState.value;
        if (prevState.isPrivate) {
          newValue = trimAgentSignature(newValue, agentSignature);
          if (agentSignature && (prevState.textareaFocused || newValue.length > 0)) {
            newValue += `\n${agentSignature}`;
          }
        } else {
          newValue = trimAgentSignature(newValue, agentSignature);
        }

        return {
          isPrivate: !prevState.isPrivate,
          value: newValue,
        };
      },
      () => {
        this.throttledSaveDraft();
      }
    );
  };

  private handleContainerClick = (): void => {
    setTimeout(() => {
      this.textareaRef?.focus();
    }, 0);
  };

  private handleTextareaChange = (value: string): void => {
    this.setState({ value }, () => {
      this.throttledSaveDraft();
    });
  };

  private handleStatusChange = (ticketStatus: TicketStatus): void => {
    this.setState({ ticketStatus }, () => {
      this.throttledSaveDraft();
    });
  };

  private handleSave = (): void => {
    const { value, isPrivate, ticketStatus, attachments } = this.state;

    // @ts-expect-error TS(2345): Argument of type 'TicketStatus | undefined' is not... Remove this comment to see the full error message
    if (formIsValid(value, isPrivate, ticketStatus, this.props.ticketStatus, attachments)) {
      this.trackCannedResponseUsage();
      this.props.onMessageSend(value, isPrivate, ticketStatus, getAttachmentsToSend(attachments));
    }
  };

  private handleTextareaFocus = (): void => {
    const { value, isPrivate } = this.state;
    const { agentSignature } = this.props;
    const messageIsCannedResponseHash = value === "#";

    if (
      agentSignature &&
      !isPrivate &&
      (messageIsCannedResponseHash || !doesMessageIncludeCustomText(value, agentSignature))
    ) {
      this.setState(
        {
          value: messageIsCannedResponseHash ? `#\n${agentSignature}` : `\n${agentSignature}`,

          textareaFocused: true,
        },
        () => {
          setTimeout(() => {
            if (this.textareaRef) {
              placeCursor(this.textareaRef, 0);
            }
          }, 0);
        }
      );
    } else {
      this.setState({
        textareaFocused: true,
      });
    }
  };

  private handleTextareaBlur = (): void => {
    this.setState({
      textareaFocused: false,
    });
  };

  private handleTextareaRefChange = (textarea: HTMLTextAreaElement): void => {
    this.textareaRef = textarea;
  };

  private handleAttachmentClick = (): void => {
    this.setState(prevState => ({
      attachments: [...prevState.attachments, null],
    }));
  };

  private handleAttachmentChange = (index: number, file: File): void => {
    this.setState(
      prevState => {
        const attachments = [...prevState.attachments];
        attachments[index] = file;
        return { attachments };
      },
      () => {
        if (this.props.onAttachmentsChange) {
          this.props.onAttachmentsChange(this.state.attachments);
        }
        trackEngagement("Tickets: Attachment added", EventPlace.Tickets, {
          "Total attachments": this.state.attachments.length,
        });
      }
    );
  };

  private handleAttachmentRemove = (_index: number): void => {
    this.setState(
      prevState => {
        const attachments = [...prevState.attachments];
        return { attachments };
      },
      () => {
        if (this.props.onAttachmentsChange) {
          this.props.onAttachmentsChange(this.state.attachments);
        }
      }
    );
  };

  private trackCannedResponseUsage = (): void => {
    const { value } = this.state;
    const tracked = trackCannedResponseUsage(value, this.usedCannedResponses, EventPlace.Tickets);
    if (tracked) {
      this.usedCannedResponses = [];
    }
  };

  private handleMouseDown = (e: React.MouseEvent<HTMLElement>): void => {
    e.stopPropagation();
  };

  private textareaRef: HTMLTextAreaElement | null = null;
  private usedCannedResponses: ICannedResponseUsage[] = [];

  render(): React.ReactElement {
    const {
      cannedResponsesByShortcuts,
      ticketStatus: originalTicketStatus,
      canSendPublicMessage,
      canSendPrivateMessage,
      isSendingMessage,
    } = this.props;
    const { value, isPrivate, attachments, ticketStatus } = this.state;
    const isDisabled = !canSendPrivateMessage && !canSendPublicMessage;

    return (
      <Wrapper>
        <MessageBox
          cannedResponsesByShortcuts={cannedResponsesByShortcuts}
          value={value}
          disabled={isDisabled}
          sendButtonLabel="Submit"
          // @ts-expect-error TS(2345): Argument of type 'TicketStatus | undefined' is not... Remove this comment to see the full error message
          sendButtonDisabled={!formIsValid(value, isPrivate, ticketStatus, originalTicketStatus, attachments)}
          sendButtonLoading={isSendingMessage}
          privateMode={isPrivate}
          maxRows={12}
          placeholder={getPlaceholder(isPrivate)}
          enableAttachments
          onFocus={this.handleTextareaFocus}
          onBlur={this.handleTextareaBlur}
          onInputMouseDown={this.handleContainerClick}
          topNodes={
            attachments.length > 0 && (
              <TicketAttachments
                attachments={attachments}
                privateMode={isPrivate}
                onChange={this.handleAttachmentChange}
                onRemove={this.handleAttachmentRemove}
              />
            )
          }
          leftNodes={
            <TicketPrivateMessageSwitch
              isPrivate={isPrivate}
              disabled={!canSendPublicMessage}
              onClick={this.handleModeChange}
              onMouseDown={this.handleMouseDown}
            />
          }
          rightNodes={
            originalTicketStatus &&
            canSendPublicMessage &&
            !isPrivate && (
              <TicketStatusDropdown
                status={ticketStatus}
                isPrivate={isPrivate}
                onChange={this.handleStatusChange}
                onMouseDown={this.handleMouseDown}
              />
            )
          }
          onInput={this.handleTextareaChange}
          onChange={this.handleTextareaChange}
          onSendButtonClick={this.handleSave}
          onAttachmentClick={this.handleAttachmentClick}
          // @ts-expect-error TS(2322): Type '(textarea: HTMLTextAreaElement) => void' is ... Remove this comment to see the full error message
          onTextareaRefChange={this.handleTextareaRefChange}
          onCannedResponseUse={this.handleCannedResponseUse}
        />
      </Wrapper>
    );
  }
}
